import { useState, useMemo } from "react";
import { useGetAllFloorsQuery } from "app/services/api/locationsApi";
import { LOCATION_ID } from "app/config";

export function useHandleHandoverFilters() {
  const locationId = LOCATION_ID;
  const { data: floors = [], isLoading: areFloorsLoading } =
    useGetAllFloorsQuery({ locationId }, { skip: !locationId });

  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [selectedEmptyFloorIds, setSelectedEmptyFloorIds] = useState<number[]>(
    [],
  );
  const [unassignedSelected, setUnassignedSelected] = useState(false);

  const toggleRoom = (roomId: number) => {
    setSelectedRoomIds((prev) =>
      prev.includes(roomId)
        ? prev.filter((id) => id !== roomId)
        : [...prev, roomId],
    );
    setUnassignedSelected(false);
  };

  const toggleFloor = (floorId: number) => {
    const floor = floors.find((f) => f.id === floorId);
    if (!floor) return;

    const roomIds = floor.rooms.map((r) => r.id);

    if (roomIds.length === 0) {
      setSelectedEmptyFloorIds((prev) =>
        prev.includes(floorId)
          ? prev.filter((id) => id !== floorId)
          : [...prev, floorId],
      );
      setUnassignedSelected(false);
      return;
    }

    setSelectedRoomIds((prev) => {
      const allSelected = roomIds.every((id) => prev.includes(id));
      return allSelected
        ? prev.filter((id) => !roomIds.includes(id))
        : [...prev, ...roomIds.filter((id) => !prev.includes(id))];
    });
    setUnassignedSelected(false);
  };

  const selectedFloors = useMemo(() => {
    return floors.filter(
      (floor) =>
        selectedEmptyFloorIds.includes(floor.id) ||
        floor.rooms.some((room) => selectedRoomIds.includes(room.id)),
    );
  }, [floors, selectedRoomIds, selectedEmptyFloorIds]);

  const selectedFloorIds = useMemo(
    () => selectedFloors.map((f) => f.id),
    [selectedFloors],
  );

  const selectedRooms = useMemo(() => {
    return floors
      .flatMap((f) => f.rooms)
      .filter((r) => selectedRoomIds.includes(r.id));
  }, [floors, selectedRoomIds]);

  const handleToggleUnassigned = () => {
    const next = !unassignedSelected;

    if (next) {
      setSelectedRoomIds([]);
      setSelectedEmptyFloorIds([]);
    }

    setUnassignedSelected(next);
  };

  return {
    floors,
    selectedRoomIds,
    selectedRooms,
    selectedFloors,
    selectedFloorIds,
    toggleRoom,
    toggleFloor,
    unassignedSelected,
    handleToggleUnassigned,
    areFloorsLoading,
  };
}
